<template>
    <v-dialog v-model="dialog" max-width="640px" persistent>
        <v-card class="booking-dialog-card">
            <div class="booking-dialog-header pa-5">
                <div class="d-flex align-center justify-space-between">
                    <div class="booking-dialog-title">
                        <v-icon class="mr-2" color="white" size="20">mdi-email-send-outline</v-icon>
                        Send Organiser Final Guest List
                    </div>
                    <v-btn icon dark @click="closeDialog()">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>
            <v-card-text class="pa-0">
                <div class="email-preview-container">
                    <div class="email-content pa-6">
                        <p class="preview-text">
                            Hi {{ lead.first_name }},
                            <br><br>
                            I’m happy to confirm that your booking is now closed with a final group size of
                            <b>{{ finalGroupSize }}</b>, and all final numbers have been sent to our suppliers.
                            <br><br>
                            You can view your final guest list here: <a :href="guestlistLink" target="_blank">{{ guestlistLink }}</a>
                            <br><br>
                            Thank you very much for your cooperation. We’ll be in touch within the next two weeks with
                            your full itinerary. In the meantime, if you need anything at all, feel free to give us a
                            call on {{ companyNumber }}
                            <br><br>
                            Thank you,
                            <br>
                            {{ advisorName }} - {{ companyDisplayName }}
                        </p>
                    </div>
                </div>
            </v-card-text>
            <v-divider />
            <v-card-actions class="pa-4 custom-buttons">
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog()">Cancel</v-btn>
                <v-btn dark color="#3988AA" :loading="sending" @click="sendEmail()">
                    <v-icon small left>mdi-send</v-icon>
                    Send
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { brandFor } from '@/utils/brand'
import { detailsAssignedTo } from '@/utils/eventPayments'

export default {
    props: {
        show: {
            type: Boolean,
            default: false
        },
        lead: {
            type: Object,
            default: () => ({})
        },
        event: {
            type: Object,
            default: () => ({ invitees: [], quotes: [], company: {} })
        }
    },
    data: () => ({
        sending: false,
    }),
    computed: {
        dialog: {
            get() {
                return this.show
            },
            set(val) {
                if (!val) this.closeDialog()
            }
        },
        companyNumber() {
            return this.event.company?.number || ''
        },
        companyDisplayName() {
            // Event type branding (Hen Party → Henit), else the company's brand / company name
            return brandFor(this.event.company, this.lead.event_type || this.event.lead?.event_type).display_name || ''
        },
        advisorName() {
            return this.lead.advisor?.name || ''
        },
        guestlistLink() {
            const company_name = this.event.company?.company_name || ''
            return `https://client.gogroupbooking.com/${company_name}/organizer/final_guestlist/${this.lead.id}`
        },
        finalGroupSize() {
            return (this.isLeadPaidInFull ? 1 : 0) + this.paidInFullInvitees.length
        },
        isLeadPaidInFull() {
            return this.hasFullPaymentTo(this.lead.id)
        },
        paidInFullInvitees() {
            return (this.event.invitees || []).filter(inv => this.isInviteePaidInFull(inv))
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.sending = false
            }
        }
    },
    methods: {
        closeDialog() {
            this.$emit('close')
        },
        // Matched by paid_to, whoever paid it.
        hasFullPaymentTo(target_id) {
            return detailsAssignedTo(this.event, target_id, this.lead).some(d => d.payment_type_id == 1)
        },
        isInviteePaidInFull(item) {
            return this.hasFullPaymentTo(item.id)
        },
        async sendEmail() {
            this.sending = true
            try {
                const payload = {
                    company_id: this.event.company?.id,
                    lead: this.lead,
                    event: this.event,
                    final_group_size: this.finalGroupSize,
                    guestlist_link: this.guestlistLink,
                }
                const result = await (await this.$axios.post('admin/events/send_organiser_final_guestlist_email', payload)).data
                if (result.response == true) {
                    this.$toast.success(result.message)
                    this.closeDialog()
                } else {
                    this.$toast.error('Failed to send final guest list email')
                }
            } catch (error) {
                this.$toast.error('Failed to send final guest list email')
            } finally {
                this.sending = false
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.booking-dialog-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.booking-dialog-title {
    font-family: Manrope;
    font-size: 16px;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
}

.email-preview-container {
    background-color: #f5f5f5;
}

.email-content {
    background-color: white;
    margin: 16px;
    border-radius: 6px;
}

.preview-text {
    font-family: Manrope;
    font-size: 14px;
    color: black;
}
</style>
